import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

/**
 * Form alanları.
 * Prototipteki tüm input'larda `<label>` ile `<input>` arasında `htmlFor`/`id`
 * bağı yoktu — ekran okuyucu alanın ne olduğunu söyleyemiyordu ve etikete
 * tıklamak alanı odaklamıyordu. Hata mesajları da `aria-describedby` ile
 * bağlanmadığı için duyurulmuyordu.
 */

/**
 * Tek girdi dili.
 *
 * Metin alanları alt-çizgili (kutusuz), ama aynı formdaki `<select>` ve
 * `date`/`time` alanları tarayıcının kendi KUTULU görünümünü kullanıyordu.
 * Sonuç: tek bir formda iki farklı alan anatomisi. Artık hepsi kutulu —
 * alt çizgi zarif ama alan sınırını belirsiz bırakıyor ve dokunmatikte
 * nereye basılacağı anlaşılmıyor.
 */
const baseInput =
  'tap-target w-full rounded-sharp border border-line-strong bg-surface px-3 py-2 ' +
  'font-medium text-ink transition-colors ' +
  'focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 ' +
  'disabled:opacity-50 placeholder:font-normal placeholder:text-ink-3';

interface FieldShellProps {
  label: string;
  htmlFor: string;
  error?: string | undefined;
  hint?: ReactNode;
  errorId: string;
  children: ReactNode;
}

function FieldShell({ label, htmlFor, error, hint, errorId, children }: FieldShellProps) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1.5 block text-label font-semibold text-ink-2">
        {label}
      </label>
      {children}
      {hint && !error && <p className="mt-1.5 text-micro text-ink-3">{hint}</p>}
      {error && (
        <p id={errorId} role="alert" className="mt-1.5 text-micro font-semibold text-risk">
          {error}
        </p>
      )}
    </div>
  );
}

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  error?: string | undefined;
  hint?: ReactNode;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, className, ...rest },
  ref,
) {
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <FieldShell label={label} htmlFor={id} error={error} hint={hint} errorId={errorId}>
      <input
        {...rest}
        id={id}
        ref={ref}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={cn(baseInput, error ? 'border-risk' : '', className)}
      />
    </FieldShell>
  );
});

interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: string;
  error?: string | undefined;
  hint?: ReactNode;
  children: ReactNode;
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, error, hint, className, children, ...rest },
  ref,
) {
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <FieldShell label={label} htmlFor={id} error={error} hint={hint} errorId={errorId}>
      <select
        {...rest}
        id={id}
        ref={ref}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={cn(baseInput, error ? 'border-risk' : '', className)}
      >
        {children}
      </select>
    </FieldShell>
  );
});

interface TextAreaFieldProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label: string;
  error?: string | undefined;
  hint?: ReactNode;
}

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  function TextAreaField({ label, error, hint, className, ...rest }, ref) {
    const id = useId();
    const errorId = `${id}-error`;

    return (
      <FieldShell label={label} htmlFor={id} error={error} hint={hint} errorId={errorId}>
        <textarea
          {...rest}
          id={id}
          ref={ref}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={cn(
            baseInput,
            'min-h-24 resize-y',
            error ? 'border-risk' : '',
            className,
          )}
        />
      </FieldShell>
    );
  },
);
